import { Box, makeStyles } from '@material-ui/core';
import { ReactNode } from 'react';

import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { StyledAccordion } from '@/components/shared/components/StyledAccordion';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface OrchestrationQuestionProps {
  index: number;
  titleTextId: string;
  questionTextId: string;
  valueTextId: string;
  expanded: boolean;
  onToggle: () => void;
  disabled?: boolean;
  children: ReactNode;
}

const useStyles = makeStyles({
  question: ({ disabled, opacity }: { disabled: boolean; opacity: number }) => ({
    opacity: disabled ? opacity : 1,
  }),
});

export const OrchestrationQuestion = ({
  index,
  titleTextId,
  questionTextId,
  valueTextId,
  expanded,
  onToggle,
  disabled = false,
  children,
}: OrchestrationQuestionProps) => {
  const { palette } = useCustomTheme();
  const classes = useStyles({ disabled, opacity: palette.action.disabledOpacity });

  return (
    <Box py={2} borderBottom={`1px solid ${palette.grey[200]}`} className={classes.question}>
      <StyledAccordion.Accordion expanded={expanded && !disabled} onChange={onToggle} disabled={disabled}>
        <StyledAccordion.AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
          <FlexRow width="100%" gridGap={16} alignItems="center">
            <Box
              width={26}
              height={26}
              borderRadius="50%"
              bgcolor={palette.secondary.main}
              color={palette.secondary.contrastText}
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Font variant="caption">
                <strong>{index}</strong>
              </Font>
            </Box>
            <Box flexGrow={1} minWidth={0} pr={2}>
              <Font variant="h5" color="title" display="block">
                <Text tId={titleTextId} />
              </Font>
              <Font variant="caption" color="textSecondary" display="block">
                <Text tId={questionTextId} />
              </Font>
            </Box>
            <Box whiteSpace="nowrap">
              <Font variant="body2">
                <strong>
                  <Text tId={valueTextId} />
                </strong>
              </Font>
            </Box>
          </FlexRow>
        </StyledAccordion.AccordionSummary>
        <StyledAccordion.AccordionDetails>
          <Box pl={5} pt={1} width="100%">
            {children}
          </Box>
        </StyledAccordion.AccordionDetails>
      </StyledAccordion.Accordion>
    </Box>
  );
};
